import { getEnvVars } from '@environment';
import {
  FONT_FAMILY_BLACK,
  FONT_FAMILY_BOLD,
  FONT_FAMILY_MEDIUM,
  FONT_FAMILY_REGULAR
} from '@unistocks/shared/consts';
import {
  useActiveColorScheme,
  useIsDarkMode
} from '@unistocks/shared/hooks/use-active-color-scheme';
import { NavigationBar } from 'expo-navigation-bar';
import {
  DarkTheme,
  DefaultTheme,
  Theme as NavigationTheme,
  ThemeProvider
} from 'expo-router/react-navigation';
import { StatusBar } from 'expo-status-bar';
import { PropsWithChildren, useEffect } from 'react';
import { Platform } from 'react-native';
import { useSafeAreaInsets } from 'react-native-safe-area-context';
import { TamaguiProvider, Theme, useTheme } from 'tamagui';
import tamaguiConfig from '../../tamagui.config';

import '@assets/global.css';
import '@assets/tamagui.generated.css';
import '@tamagui/core/reset.css';

const { theme } = getEnvVars();

/**
 * Configures the Tamagui theme provider
 * and the react-navigation theme provider
 */
export function AppThemeProvider({ children }: PropsWithChildren) {
  const insets = useSafeAreaInsets();
  const { activeColorScheme } = useActiveColorScheme();

  return (
    <TamaguiProvider
      disableInjectCSS
      config={tamaguiConfig}
      defaultTheme={activeColorScheme as string}
      insets={insets}
    >
      <Theme name={theme}>
        <NavigationThemeProvider>{children}</NavigationThemeProvider>
      </Theme>
    </TamaguiProvider>
  );
}

/**
 * Configures the react-navigation theme
 * Needs to be wrapped in a TamaguiProvider to be able to use the theme colors
 */
const NavigationThemeProvider: React.FC<PropsWithChildren> = ({ children }) => {
  const isDarkMode = useIsDarkMode();
  const { borderColor, bodyFont, unistocks } = useTheme();

  /**
   * Update system colors to match theme
   */
  useEffect(() => {
    if (Platform.OS === 'android') {
      StatusBar.setStyle(isDarkMode ? 'light' : 'dark', true);
      // TODO Why is this still needed? Statusbar is not set when changing theme
      // setBackgroundColor('transparent', true);

      NavigationBar.setStyle(isDarkMode ? 'light' : 'dark');
    }
  }, [isDarkMode]);

  const navigationTheme: NavigationTheme = {
    dark: isDarkMode,
    colors: {
      ...(isDarkMode ? DarkTheme.colors : DefaultTheme.colors),
      background: 'transparent',
      card: 'transparent',
      border: borderColor.val,
      primary: unistocks.val,
      text: bodyFont.val,
      notification: isDarkMode ? DarkTheme.colors.notification : DefaultTheme.colors.notification
    },
    fonts: {
      regular: {
        fontFamily: FONT_FAMILY_REGULAR,
        fontWeight: '400'
      },
      medium: {
        fontFamily: FONT_FAMILY_MEDIUM,
        fontWeight: '500'
      },
      bold: {
        fontFamily: FONT_FAMILY_BOLD,
        fontWeight: '700'
      },
      heavy: {
        fontFamily: FONT_FAMILY_BLACK,
        fontWeight: '900'
      }
    }
  };

  return <ThemeProvider value={navigationTheme}>{children}</ThemeProvider>;
};
